// Localização: frontend/src/hooks/useFinanceiro.js
// Hook customizado para o módulo financeiro

import { useState, useEffect, useCallback } from 'react';
import financeiroApi from '../api/financeiroApi';
import { toast } from 'react-toastify';

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export const useFinanceiro = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleRequest = useCallback(async (requestFn, successMessage = null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await requestFn();
            if (successMessage) {
                toast.success(successMessage);
            }
            return response.data;
        } catch (err) {
            // Tratamento especial para erros de rede
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || !err.response) {
                const networkError = 'Erro de conexão. Verifique se o servidor está rodando.';
                setError(networkError);
                console.error('[handleRequest] Erro de rede:', err);
                // Não mostrar toast para erros de rede silenciosamente
                // toast.error(networkError);
                throw err;
            }
            
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao processar requisição';
            setError(errorMessage);
            toast.error(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        error,
        handleRequest
    };
};

// ============================================================================
// HOOKS ESPECÍFICOS
// ============================================================================

// Contas Bancárias
export const useContasBancarias = () => {
    const { loading, error, handleRequest } = useFinanceiro();
    const [contas, setContas] = useState([]);

    const fetchContas = useCallback(async (params = {}) => {
        try {
            const data = await handleRequest(() => financeiroApi.contaBancaria.getAll(params));
            setContas(data.data || data || []);
            return data;
        } catch (err) {
            console.error('[useContasBancarias] Erro ao buscar contas:', err);
            // Se for erro de rede, definir array vazio para não quebrar a UI
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || !err.response) {
                setContas([]);
                console.error('[useContasBancarias] Erro de rede - verifique se o servidor está rodando');
                // Retornar objeto vazio em vez de lançar erro para não quebrar a UI
                return { data: [] };
            }
            // Para outros erros, definir array vazio também para não quebrar
            setContas([]);
            throw err;
        }
    }, [handleRequest]);

    const createConta = async (data) => {
        return await handleRequest(() => financeiroApi.contaBancaria.create(data), 'Conta bancária criada com sucesso');
    };

    const updateConta = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaBancaria.update(id, data), 'Conta bancária atualizada com sucesso');
    };

    const deleteConta = async (id) => {
        return await handleRequest(() => financeiroApi.contaBancaria.delete(id), 'Conta bancária deletada com sucesso');
    };

    return {
        contas,
        loading,
        error,
        fetchContas,
        createConta,
        updateConta,
        deleteConta
    };
};

// Categorias
export const useCategorias = () => {
    const { loading, error, handleRequest } = useFinanceiro();
    const [categorias, setCategorias] = useState([]);

    const fetchCategorias = useCallback(async (params = {}) => {
        try {
            const data = await handleRequest(() => financeiroApi.categoria.getAll(params));
            setCategorias(data.data || data || []);
            return data;
        } catch (err) {
            console.error('[useCategorias] Erro ao buscar categorias:', err);
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
                setCategorias([]);
                console.error('[useCategorias] Erro de rede - verifique se o servidor está rodando');
            }
            throw err;
        }
    }, [handleRequest]);

    const createCategoria = async (data) => {
        return await handleRequest(() => financeiroApi.categoria.create(data), 'Categoria criada com sucesso');
    };

    const updateCategoria = async (id, data) => {
        return await handleRequest(() => financeiroApi.categoria.update(id, data), 'Categoria atualizada com sucesso');
    };

    const deleteCategoria = async (id) => {
        return await handleRequest(() => financeiroApi.categoria.delete(id), 'Categoria deletada com sucesso');
    };

    return {
        categorias,
        loading,
        error,
        fetchCategorias,
        createCategoria,
        updateCategoria,
        deleteCategoria
    };
};

// Subcategorias
export const useSubcategorias = () => {
    const { loading, error, handleRequest } = useFinanceiro();
    const [subcategorias, setSubcategorias] = useState([]);

    const fetchSubcategorias = useCallback(async (params = {}) => {
        try {
            const data = await handleRequest(() => financeiroApi.subcategoria.getAll(params));
            setSubcategorias(data.data || data || []);
            return data;
        } catch (err) {
            console.error('[useSubcategorias] Erro ao buscar subcategorias:', err);
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
                setSubcategorias([]);
                console.error('[useSubcategorias] Erro de rede - verifique se o servidor está rodando');
            }
            throw err;
        }
    }, [handleRequest]);

    const createSubcategoria = async (data) => {
        return await handleRequest(() => financeiroApi.subcategoria.create(data), 'Subcategoria criada com sucesso');
    };

    const updateSubcategoria = async (id, data) => {
        return await handleRequest(() => financeiroApi.subcategoria.update(id, data), 'Subcategoria atualizada com sucesso');
    };

    const deleteSubcategoria = async (id) => {
        return await handleRequest(() => financeiroApi.subcategoria.delete(id), 'Subcategoria deletada com sucesso');
    };

    return {
        subcategorias,
        loading,
        error,
        fetchSubcategorias,
        createSubcategoria,
        updateSubcategoria,
        deleteSubcategoria
    };
};

// Contas a Receber
export const useContasReceber = () => {
    const { loading, error, handleRequest } = useFinanceiro();
    const [contas, setContas] = useState([]);
    const [pagination, setPagination] = useState(null);

    const fetchContas = useCallback(async (params = {}) => {
        try {
            const response = await handleRequest(() => financeiroApi.contaReceber.getAll(params));
            const data = response.data || response;
            setContas(data.data || data || []);
            if (data.pagination) {
                setPagination(data.pagination);
            } else {
                setPagination(null);
            }
            return data;
        } catch (err) {
            console.error('[useContasReceber] Erro ao buscar contas:', err);
            // Se for erro de rede, definir array vazio para não quebrar a UI
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || !err.response) {
                setContas([]);
                console.error('[useContasReceber] Erro de rede - verifique se o servidor está rodando');
                // Retornar objeto vazio em vez de lançar erro para não quebrar a UI
                return { data: [], pagination: null };
            }
            // Para outros erros, definir array vazio também para não quebrar
            setContas([]);
            setPagination(null);
            throw err;
        }
    }, [handleRequest]);

    const createConta = async (data) => {
        return await handleRequest(() => financeiroApi.contaReceber.create(data), 'Conta a receber criada com sucesso');
    };

    const updateConta = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaReceber.update(id, data), 'Conta a receber atualizada com sucesso');
    };

    const deleteConta = async (id) => {
        return await handleRequest(() => financeiroApi.contaReceber.delete(id), 'Conta a receber deletada com sucesso');
    };

    const receber = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaReceber.receber(id, data), 'Recebimento registrado com sucesso');
    };

    const receberParcial = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaReceber.receberParcial(id, data), 'Recebimento parcial registrado com sucesso');
    };

    return {
        contas,
        pagination,
        loading,
        error,
        fetchContas,
        createConta,
        updateConta,
        deleteConta,
        receber,
        receberParcial
    };
};

// Contas a Pagar
export const useContasPagar = () => {
    const { loading, error, handleRequest } = useFinanceiro();
    const [contas, setContas] = useState([]);

    const fetchContas = async (params = {}) => {
        const data = await handleRequest(() => financeiroApi.contaPagar.getAll(params));
        setContas(data.data || data);
        return data;
    };

    const createConta = async (data) => {
        return await handleRequest(() => financeiroApi.contaPagar.create(data), 'Conta a pagar criada com sucesso');
    };

    const updateConta = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaPagar.update(id, data), 'Conta a pagar atualizada com sucesso');
    };

    const deleteConta = async (id) => {
        return await handleRequest(() => financeiroApi.contaPagar.delete(id), 'Conta a pagar deletada com sucesso');
    };

    const pagar = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaPagar.pagar(id, data), 'Pagamento registrado com sucesso');
    };

    const pagarParcial = async (id, data) => {
        return await handleRequest(() => financeiroApi.contaPagar.pagarParcial(id, data), 'Pagamento parcial registrado com sucesso');
    };

    return {
        contas,
        loading,
        error,
        fetchContas,
        createConta,
        updateConta,
        deleteConta,
        pagar,
        pagarParcial
    };
};

// Dashboard
export const useDashboardFinanceiro = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [resumo, setResumo] = useState(null);
    const [fluxoCaixa, setFluxoCaixa] = useState([]);
    const [porCategoria, setPorCategoria] = useState([]);
    const [previsao, setPrevisao] = useState(null);

    const fetchResumo = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await financeiroApi.dashboard.getResumo();
            const data = response.data?.data || response.data || response;
            setResumo(data);
            return data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar resumo financeiro';
            setError(errorMessage);
            console.error('Erro ao buscar resumo:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const fetchFluxoCaixa = async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await financeiroApi.dashboard.getFluxoCaixa(params);
            const responseData = response.data?.data || response.data || response;
            // Se a resposta incluir saldo_inicial, adicionar aos dados
            const data = Array.isArray(responseData) ? responseData : [];
            const saldoInicial = response.data?.saldo_inicial;
            
            // Adicionar saldo_inicial ao primeiro item do array para facilitar acesso
            if (data.length > 0 && saldoInicial !== undefined) {
                data[0].saldo_inicial = saldoInicial;
            }
            
            setFluxoCaixa(data);
            return { data, saldo_inicial: saldoInicial };
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar fluxo de caixa';
            setError(errorMessage);
            console.error('Erro ao buscar fluxo de caixa:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const fetchPorCategoria = async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await financeiroApi.dashboard.getPorCategoria(params);
            const data = response.data?.data || response.data || response;
            setPorCategoria(Array.isArray(data) ? data : []);
            return data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar dados por categoria';
            setError(errorMessage);
            console.error('Erro ao buscar dados por categoria:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const fetchPrevisao = async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await financeiroApi.dashboard.getPrevisao(params);
            const data = response.data?.data || response.data || response;
            setPrevisao(data);
            return data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar previsões';
            setError(errorMessage);
            console.error('Erro ao buscar previsões:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const [contasPagar, setContasPagar] = useState([]);
    const [contasReceber, setContasReceber] = useState([]);

    const fetchContasPagar = async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await financeiroApi.dashboard.getContasPagar(params);
            const responseData = response.data?.data || response.data || response;
            const data = Array.isArray(responseData) ? responseData : [];
            setContasPagar(data);
            return data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar contas a pagar';
            setError(errorMessage);
            console.error('Erro ao buscar contas a pagar:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const fetchContasReceber = async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await financeiroApi.dashboard.getContasReceber(params);
            const responseData = response.data?.data || response.data || response;
            const data = Array.isArray(responseData) ? responseData : [];
            setContasReceber(data);
            return data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar contas a receber';
            setError(errorMessage);
            console.error('Erro ao buscar contas a receber:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        resumo,
        fluxoCaixa,
        porCategoria,
        previsao,
        contasPagar,
        contasReceber,
        loading,
        error,
        fetchResumo,
        fetchFluxoCaixa,
        fetchPorCategoria,
        fetchPrevisao,
        fetchContasPagar,
        fetchContasReceber
    };
};

